import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories } from "@/mocks/categories";

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于磁力搜索指南",
  description:
    "磁力搜索指南致力于为中文用户提供最全面、最专业的磁力搜索知识，帮助用户掌握磁力搜索技巧、了解最佳工具、保护网络安全。",
};

const contactJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "磁力搜索指南",
  url: "https://cili-sousuo.com",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    availableLanguage: ["Chinese"],
  },
};

export default function AboutPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    website_alt: "",
  });
  const [formStatus, setFormStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const honeypotValue = formData.website_alt.trim();
    if (honeypotValue) {
      setFormStatus("success");
      setFormData({ name: "", email: "", message: "", website_alt: "" });
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    try {
      const form = e.currentTarget;
      const payload = new FormData(form);
      payload.delete("website_alt");

      const response = await fetch(
        "https://readdy.ai/api/form/d9q9flknlsngrm4llts0",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(
            payload as unknown as Record<string, string>
          ).toString(),
        }
      );

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // 非JSON响应
      }

      const code =
        typeof parsed?.code === "string" ? parsed.code : "";
      const serverMsg =
        typeof parsed?.meta?.message === "string"
          ? parsed.meta.message
          : typeof parsed?.message === "string"
            ? parsed.message
            : "";

      if (
        response.ok &&
        code === "OK"
      ) {
        setFormStatus("success");
        setFormData({ name: "", email: "", message: "", website_alt: "" });
      } else {
        const errMsg =
          serverMsg ||
          responseText ||
          "提交失败，请稍后重试";
        setFormError(errMsg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络连接后重试");
      setFormStatus("error");
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(aboutJsonLd),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(contactJsonLd),
        }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 py-4">
            <Breadcrumb
              items={[{ label: "首页", href: "/" }, { label: "关于我们" }]}
            />
          </div>
          <div className="w-full px-4 md:px-6 py-10 md:py-14">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
              关于<span className="text-primary-500">磁力搜索指南</span>
            </h1>
            <p className="text-foreground-500 text-base max-w-2xl leading-relaxed">
              致力于为中文用户提供最全面、最专业的磁力搜索知识平台
            </p>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20team%20collaborating%2C%20warm%20ambient%20lighting%2C%20minimalist%20office%20design%2C%20wooden%20desk%20with%20laptops%2C%20professional%20atmosphere%2C%20cream%20and%20terracotta%20tones&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="磁力搜索指南团队"
                    title="磁力搜索指南 - 专业团队"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  我们的使命
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  磁力搜索指南创立于2024年，是中文互联网上最全面的磁力搜索知识平台。我们的团队由资深的P2P技术研究者和内容创作者组成，致力于帮助普通用户更好地理解和使用磁力搜索技术。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  在这个信息爆炸的时代，如何高效、安全地获取所需资源是每个互联网用户都面临的挑战。我们通过系统化的教程、实用的工具推荐和全面的安全指南，让磁力搜索不再神秘。
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  我们坚信知识应该自由传播。我们的所有内容均免费开放，持续更新，帮助更多人掌握磁力搜索这一高效工具。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats / Reputation */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                平台数据
              </h2>
              <p className="text-foreground-500 leading-relaxed">
                用数据说话，我们的努力看得见
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                {
                  value: "50+",
                  label: "精品教程",
                  icon: "ri-article-line",
                },
                {
                  value: `${categories.length}`,
                  label: "资源分类",
                  icon: "ri-stack-line",
                },
                {
                  value: "100K+",
                  label: "月访问用户",
                  icon: "ri-user-line",
                },
                {
                  value: "4.9",
                  label: "用户评分",
                  icon: "ri-star-line",
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 bg-background-50 border border-background-200/70 rounded-xl"
                >
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-accent-100/80 flex items-center justify-center">
                    <i className={`${stat.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <div className="text-3xl font-bold text-accent-600 font-heading mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                联系我们
              </h2>
              <p className="text-foreground-500 leading-relaxed">
                有任何关于磁力搜索的问题或建议？欢迎随时与我们联系
              </p>
            </div>

            {formStatus === "success" ? (
              <div className="text-center py-12 bg-background-50 border border-accent-200/70 rounded-xl">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-accent-600"></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">
                  提交成功！
                </h3>
                <p className="text-foreground-500">
                  感谢您的留言，我们会尽快回复您
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                data-readdy-form="true"
              >
                {formStatus === "error" && formError && (
                  <div className="p-4 bg-primary-50 border border-primary-200/60 rounded-xl text-sm text-primary-700">
                    {formError}
                  </div>
                )}

                <div>
                  <label
                    htmlFor="about-name"
                    className="block text-sm font-medium text-foreground-700 mb-2"
                  >
                    您的姓名
                  </label>
                  <input
                    id="about-name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100/50 transition-all duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label
                    htmlFor="about-email"
                    className="block text-sm font-medium text-foreground-700 mb-2"
                  >
                    电子邮箱
                  </label>
                  <input
                    id="about-email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100/50 transition-all duration-200"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>

                <div>
                  <label
                    htmlFor="about-message"
                    className="block text-sm font-medium text-foreground-700 mb-2"
                  >
                    留言内容
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100/50 transition-all duration-200 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">
                    {formData.message.length}/500
                  </p>
                </div>

                <div className="honeypot-field">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    value={formData.website_alt}
                    onChange={(e) =>
                      setFormData({ ...formData, website_alt: e.target.value })
                    }
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full px-6 py-3.5 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap"
                >
                  {formStatus === "submitting" ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : (
                    "发送留言"
                  )}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}